/**
 * The deliberately plain pause surface.
 *
 * A fullscreen PWA has no browser chrome to fall back on. On a tablet that made
 * the old Escape-only pause path equivalent to having no pause path at all:
 * once a player had started, there was no visible way to stop or leave. This
 * module keeps the DOM plumbing separate from `main.ts`, where pausing the
 * simulation still belongs because that is the only place that owns all of its
 * moving parts.
 */

import {
  compositeOver,
  ensureContrast,
} from '@/render/contrast';
import type { ZonePalette } from '@/render/palette';

const OVERLAY_ID = 'rosalie-pause';
const SCRIM_ALPHA = 0.96;
/** Text is WCAG AAA; a 1 px hairline nobody can see is not a border (AA non-text). */
const TEXT_CONTRAST = 7;
const BORDER_CONTRAST = 3;
/**
 * Aim this far above the line, because the two sides measure differently.
 *
 * `compositeOver` returns a packed 8-bit colour, so the scrim-over-white
 * background this file solves against is rounded; the browser composites the
 * real thing at higher precision and `pause.spec.ts` reads it back that way.
 * The two disagree by up to half a code per channel, which moved four zones to
 * 6.98-6.99 against a 7 they had been solved to exactly — green arithmetic, red
 * gate, and the gate was right.
 *
 * 0.15 is comfortably more than that half-code and far less than a step anybody
 * can see. It is not a fudge factor: without it this file promises a ratio it
 * only reaches when the rounding happens to fall the helpful way.
 */
const QUANTISATION_MARGIN = 0.15;
const BLACK = 0x000000;
const WHITE = 0xffffff;

interface PauseColours {
  body: number;
  border: number;
  primaryBackground: number;
  primaryLabel: number;
}

function colour(value: number, alpha = 1): string {
  const red = (value >> 16) & 0xff;
  const green = (value >> 8) & 0xff;
  const blue = value & 0xff;
  return `rgb(${red} ${green} ${blue} / ${alpha})`;
}

export interface PauseOverlayHooks {
  onResume(): void;
  /**
   * Throw the save away and start the game again from the beginning.
   *
   * D-053 requires this to exist **before the first checkpoint is ever
   * written**, and that is not a nicety: a build that saves and cannot restart
   * is a build that can only be played from wherever the reviewer last got to.
   * The two halves ship in one commit, which is why this hook is here and not
   * in a follow-up.
   */
  onRestart(): void;
}

/** A small, dependency-free overlay that never owns simulation state itself. */
export class PauseOverlay {
  #overlay: HTMLDivElement | null = null;
  #detail: HTMLParagraphElement | null = null;
  /** Whether the restart button is one press from firing. See `#restartButton`. */
  #armed = false;
  /** Kept so showing the overlay can put its label back. */
  #restart: HTMLButtonElement | null = null;

  constructor(
    private readonly palette: ZonePalette,
    private readonly hooks: PauseOverlayHooks,
  ) {}

  attach(): void {
    let overlay = document.getElementById(OVERLAY_ID) as HTMLDivElement | null;
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Game paused');
      overlay.style.cssText = [
        'position:fixed',
        'inset:0',
        'z-index:30',
        'display:none',
        'align-items:center',
        'justify-content:center',
        'padding:calc(env(safe-area-inset-top) + 2rem) calc(env(safe-area-inset-right) + 2rem) calc(env(safe-area-inset-bottom) + 2rem) calc(env(safe-area-inset-left) + 2rem)',
        `background:${colour(this.palette.shadow, SCRIM_ALPHA)}`,
        `color:${colour(this.#colours.body)}`,
        'font:16px/1.5 system-ui,sans-serif',
        'text-align:center',
      ].join(';');

      const panel = document.createElement('div');
      panel.style.cssText = [
        'display:flex',
        'flex-direction:column',
        'align-items:center',
        'gap:1rem',
        'width:min(100%, 25rem)',
        'padding:2rem',
        `border:1px solid ${colour(this.#colours.border)}`,
      ].join(';');

      const title = document.createElement('h1');
      title.textContent = 'Paused';
      title.style.cssText = 'margin:0;font:500 1.4rem/1.2 system-ui,sans-serif;letter-spacing:0.08em;text-transform:uppercase';
      panel.appendChild(title);

      const detail = document.createElement('p');
      // This line carries the restart and exit consequences when it is not
      // blank. Fading it with element opacity would reintroduce the contrast
      // failure after the colour helper had met AAA on paper.
      detail.style.cssText = 'margin:0;max-width:20rem';
      panel.appendChild(detail);
      this.#detail = detail;

      const actions = document.createElement('div');
      actions.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;gap:0.75rem;margin-top:0.5rem';
      actions.append(this.#resumeButton(), this.#restartButton(), this.#exitButton());
      panel.append(actions);
      overlay.append(panel);
      (document.body ?? document.documentElement).append(overlay);
    }
    this.#overlay = overlay;
    this.setPaused(false);
  }

  setPaused(paused: boolean): void {
    const overlay = this.#overlay;
    if (!overlay) return;
    overlay.style.display = paused ? 'flex' : 'none';
    overlay.setAttribute('aria-hidden', String(!paused));
    // Deliberately blank on pause. The line here was "The farm is waiting." —
    // written by the sub-agent that built this overlay, which is not its call to
    // make (spec §0: Codex never makes narrative decisions) and is not the
    // game's voice anyway. Spec §7: told ENTIRELY without words. A pause screen
    // is chrome rather than story and is allowed to say "Resume", but the moment
    // it starts narrating, the game has a narrator.
    //
    // The element stays because §9 wants a hand-lettered pause screen and this
    // is where its content will go once that art exists.
    if (paused && this.#detail) this.#detail.textContent = '';
    // Showing the overlay disarms the restart, label and all. See `#restartButton`.
    if (paused) {
      this.#armed = false;
      if (this.#restart) this.#restart.textContent = 'Start again';
    }
  }

  #resumeButton(): HTMLButtonElement {
    const button = this.#shell('Resume', true);
    button.addEventListener('click', () => {
      this.#armed = false;
      this.hooks.onResume();
    });
    return button;
  }

  /**
   * Restart, behind a second press.
   *
   * A confirm rather than a dialog because the pause screen is already the
   * dialog, and because the thing being confirmed is small enough to say on the
   * button itself. The arming resets whenever the overlay is shown again, so a
   * player who half-pressed it, resumed, and came back an hour later does not
   * find a loaded trigger under their thumb.
   */
  #restartButton(): HTMLButtonElement {
    const button = this.#shell('Start again', false);
    button.dataset['pause'] = 'restart';
    this.#restart = button;
    button.addEventListener('click', () => {
      if (!this.#armed) {
        this.#armed = true;
        button.textContent = 'Confirm — lose your place';
        if (this.#detail) this.#detail.textContent = 'Starting again discards the beat you had reached. Your settings are kept.';
        return;
      }
      this.hooks.onRestart();
    });
    return button;
  }

  #exitButton(): HTMLButtonElement {
    const button = this.#shell('Exit', false);
    button.addEventListener('click', () => {
      this.#armed = false;
      // Browsers only honour close() for windows they consider script-opened.
      // We still try it because an installed PWA may allow it, but leave an
      // explicit, usable instruction behind when ordinary tabs refuse silently.
      window.close();
      if (this.#detail) {
        this.#detail.textContent = 'ROSALIE remains paused. You can close this tab or leave the installed app now.';
      }
    });
    return button;
  }

  #shell(label: string, primary: boolean): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.style.cssText = [
      'appearance:none',
      'min-width:7.5rem',
      'padding:0.65rem 1.25rem',
      `border:1px solid ${colour(this.#colours.border)}`,
      `background:${primary ? colour(this.#colours.primaryBackground) : 'transparent'}`,
      `color:${primary ? colour(this.#colours.primaryLabel) : colour(this.#colours.body)}`,
      'border-radius:0',
      'box-shadow:none',
      'font:500 0.95rem/1 system-ui,sans-serif',
      'letter-spacing:0.04em',
      'cursor:pointer',
    ].join(';');
    return button;
  }

  get #colours(): PauseColours {
    // A translucent scrim does not have one background colour: a pause opened
    // over lightning and one opened over the yard's unlit ground composite
    // differently. 0.88 left the outside zone's brightest possible composite
    // below AAA even for white text. At 0.96, shadow over WHITE is the honest
    // worst case and still keeps a little of the interrupted frame visible.
    // `pause.spec.ts` composites this actual CSS alpha over both black and white
    // so a later opacity reduction cannot make this promise silently false.
    const worstScrim = compositeOver(this.palette.shadow, WHITE, SCRIM_ALPHA);
    const text = TEXT_CONTRAST + QUANTISATION_MARGIN;
    const edge = BORDER_CONTRAST + QUANTISATION_MARGIN;
    const primaryBackground = ensureContrast(this.palette.fog, BLACK, text);
    return {
      body: ensureContrast(this.palette.fog, worstScrim, text),
      border: ensureContrast(this.palette.fill, worstScrim, edge),
      // A mid-tone fog cannot ever carry a 7:1 label: neither black nor white
      // reaches it. Lift the same fog hue to the light side of AAA first, then
      // draw the label toward its darker end. The old raw pair was exactly this
      // trap in the service corridor, not merely a yard-only dark-mode defect.
      primaryBackground,
      primaryLabel: ensureContrast(this.palette.shadow, primaryBackground, text),
    };
  }
}
